import { Component, OnInit, OnDestroy, inject, signal, computed, Injector, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UserService } from '../../../services/user.service';
import { AuthService } from '../../../services/auth.service';
import { UserProfileDisplayService } from '../../../services/user-profile-display.service';
import { DepartmentMasterService } from '../../../services/department-master.service';
import { DepartmentSchemesService } from '../../../services/department-schemes.service';
import { LocationMasterService } from '../../../services/location-master.service';
import { User, UserRole, Department, UserAssignmentAuditEntry } from '../../../models/user.model';
import { DataTableComponent, TableColumn } from '../../../components/shared/data-table/data-table.component';
import { AdminModalComponent } from '../../../components/shared/admin-modal/admin-modal.component';
import * as XLSX from 'xlsx';
import {
  Firestore,
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  query,
  where,
  getDocs,
  serverTimestamp,
  deleteDoc,
  collectionData,
  docData,
  Timestamp,
} from '@angular/fire/firestore';
import { runInInjectionContext } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-user-management',
  standalone: true,
  imports: [
    CommonModule, 
    FormsModule,
    ReactiveFormsModule, 
    AdminModalComponent
  ],
  templateUrl: './user-management.component.html',
  styleUrls: ['./user-management.component.scss']
})
export class UserManagementComponent implements OnInit, OnDestroy {
  private userService = inject(UserService);
  private authService = inject(AuthService);
  readonly profileUi = inject(UserProfileDisplayService);
  private deptService = inject(DepartmentMasterService);
  private deptSchemesService = inject(DepartmentSchemesService);
  private locationMaster = inject(LocationMasterService);
  private fb = inject(FormBuilder);
  private firestore = inject(Firestore);
  private injector = inject(Injector);
  private usersCollection = collection(this.firestore, 'users');
  private destroy$ = new Subject<void>();
  private readonly legacyDepartmentMigrationKey = 'ppms_legacy_department_migration_v1';
  Math = Math;

  private run<T>(fn: () => T): T {
    return runInInjectionContext(this.injector, fn);
  }

  users = signal<User[]>([]);
  departments = signal<Department[]>([]);
  masterLocations = computed(() => this.locationMaster.activeLocations());
  filteredUsers = computed(() => {
    let users = this.users();
    const searchQuery = this.searchQuery().toLowerCase();
    
    // Apply search filter
    if (searchQuery) {
      users = users.filter(user => 
        user.displayName.toLowerCase().includes(searchQuery) ||
        user.email.toLowerCase().includes(searchQuery) ||
        user.role.toLowerCase().includes(searchQuery) ||
        (user.employeeId && user.employeeId.toLowerCase().includes(searchQuery))
      );
    }
    
    // Apply role filter (mobile KPI chips)
    const roleFilter = this.roleFilter();
    if (roleFilter) {
      users = users.filter(user => user.role === roleFilter);
    }

    // Apply inactive filter
    if (!this.showInactiveUsers()) {
      users = users.filter(user => user.isActive);
    }
    
    // Apply sorting
    const sortField = this.sortField();
    const sortDirection = this.sortDirection();
    
    users.sort((a, b) => {
      let aValue: any;
      let bValue: any;
      
      switch (sortField) {
        case 'displayName':
          aValue = a.displayName?.toLowerCase() || '';
          bValue = b.displayName?.toLowerCase() || '';
          break;
        case 'email':
          aValue = a.email?.toLowerCase() || '';
          bValue = b.email?.toLowerCase() || '';
          break;
        case 'role':
          aValue = a.role || '';
          bValue = b.role || '';
          break;
        case 'mobileNumber':
          aValue = a.mobileNumber || '';
          bValue = b.mobileNumber || '';
          break;
        case 'isActive':
          aValue = a.isActive ? 1 : 0;
          bValue = b.isActive ? 1 : 0;
          break;
        case 'createdAt':
          aValue = a.createdAt ? (a.createdAt instanceof Date ? a.createdAt.getTime() : (a.createdAt.toMillis ? a.createdAt.toMillis() : new Date(a.createdAt).getTime())) : 0;
          bValue = b.createdAt ? (b.createdAt instanceof Date ? b.createdAt.getTime() : (b.createdAt.toMillis ? b.createdAt.toMillis() : new Date(b.createdAt).getTime())) : 0;
          break;
        case 'lastLogin':
          aValue = a.lastLogin ? (a.lastLogin instanceof Date ? a.lastLogin.getTime() : (a.lastLogin.toMillis ? a.lastLogin.toMillis() : new Date(a.lastLogin).getTime())) : 0;
          bValue = b.lastLogin ? (b.lastLogin instanceof Date ? b.lastLogin.getTime() : (b.lastLogin.toMillis ? b.lastLogin.toMillis() : new Date(b.lastLogin).getTime())) : 0;
          break;
        default:
          aValue = a.createdAt ? (a.createdAt instanceof Date ? a.createdAt.getTime() : (a.createdAt.toMillis ? a.createdAt.toMillis() : new Date(a.createdAt).getTime())) : 0;
          bValue = b.createdAt ? (b.createdAt instanceof Date ? b.createdAt.getTime() : (b.createdAt.toMillis ? b.createdAt.toMillis() : new Date(b.createdAt).getTime())) : 0;
      }
      
      if (sortDirection === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
    
    return users;
  });
  
  admins = computed(() => this.users().filter(u => u.role === UserRole.ADMIN));
  ministers = computed(() => this.users().filter(u => u.role === UserRole.MINISTER));
  osds = computed(() => this.users().filter(u => u.role === UserRole.OSD));
  operators = computed(() => this.users().filter(u => u.role === UserRole.OPERATOR));
  approvers = computed(() => this.users().filter(u => u.role === UserRole.APPROVER));
  
  loading = signal(false);
  isModalOpen = signal(false);
  isDeleteModalOpen = signal(false);
  isSubmitting = signal(false);
  isDeleting = signal(false);
  searchQuery = signal('');
  roleFilter = signal('');
  userToDelete = signal<User | null>(null);

  currentUser = computed(() => this.authService.currentUser());

  // View state management
  showInactiveUsers = signal(true);
  sortField = signal<string>('createdAt');
  sortDirection = signal<'asc' | 'desc'>('desc');

  // Computed properties for statistics
  totalAdmins = computed(() => this.users().filter(u => u.role === UserRole.ADMIN).length);
  totalMinisters = computed(() => this.users().filter(u => u.role === UserRole.MINISTER).length);
  totalOsds = computed(() => this.users().filter(u => u.role === UserRole.OSD).length);
  totalOperators = computed(() => this.users().filter(u => u.role === UserRole.OPERATOR).length);
  totalApprovers = computed(() => this.users().filter(u => u.role === UserRole.APPROVER).length);

  // Enhanced pagination
  itemsPerPage = signal(10);
  currentPage = signal(1);
  pageSize = signal(10);

  // Computed properties for enhanced view
  paginatedUsers = computed(() => {
    const filtered = this.filteredUsers();
    const start = (this.currentPage() - 1) * this.itemsPerPage();
    const end = start + this.itemsPerPage();
    return filtered.slice(start, end);
  });

  totalPages = computed(() => {
    const pages = Math.ceil(this.filteredUsers().length / this.itemsPerPage());
    return Array.from({ length: pages }, (_, i) => i + 1);
  });
  startIndex = computed(() => (this.currentPage() - 1) * this.itemsPerPage());
  endIndex = computed(() => Math.min(this.currentPage() * this.itemsPerPage(), this.filteredUsers().length));

  
  userForm: FormGroup;
  editingUser: User | null = null;
  isBlockMenuOpen = signal(false);
  currentUserRole = signal<UserRole | null>(null);
  currentUserId = signal<string | null>(null);

  columns: TableColumn[] = [
    { key: 'displayName', label: 'Full Name', sortable: true },
    { key: 'email', label: 'Email', sortable: true },
    { key: 'role', label: 'Role', type: 'badge' },
    { key: 'departmentIds', label: 'Assigned Department', type: 'text' },
    { key: 'status', label: 'Status', type: 'badge' },
    { key: 'lastLogin', label: 'Last Updated', type: 'date' },
    { key: 'actions', label: 'Actions', type: 'actions' }
  ];

  constructor() {
    this.userForm = this.fb.group({
      displayName: ['', [Validators.required, Validators.minLength(3)]],
      email: ['', [Validators.required, Validators.email]],
      mobileNumber: ['', [Validators.required, Validators.pattern(/^[0-9]{10}$/)]],
      employeeId: [''],
      role: [UserRole.OPERATOR, Validators.required],
      departmentIds: [[]],
      blockAccess: [[]],
      status: ['active', Validators.required],
      remarks: [''],
      password: ['', [Validators.minLength(6)]],
    }, { validators: [this.departmentRequiredValidator, this.blockAccessRequiredValidator] });
  }

  blockAccessRequiredValidator(g: FormGroup) {
    const role = g.get('role')?.value;
    if (role === UserRole.OSD || role === UserRole.ADMIN || role === UserRole.MINISTER) {
      return null;
    }
    const blocks = g.get('blockAccess')?.value;
    if (!blocks || blocks.length === 0) {
      return { blockAccessRequired: true };
    }
    return null;
  }

  departmentRequiredValidator(g: FormGroup) {
    const role = g.get('role')?.value;
    if (role === UserRole.OSD || role === UserRole.ADMIN || role === UserRole.MINISTER) {
      return null;
    }
    const depts = g.get('departmentIds')?.value;
    if (!depts || depts.length === 0) {
      return { departmentRequired: true };
    }
    return null;
  }

  
  /**
   * Export EVERY user in the system to an Excel workbook with one sheet per role
   * (plus an "All Users" overview sheet). Ignores the current search / inactive
   * filter so the export is always the complete dataset.
   */
  exportEnhancedData(): void {
    const allUsers = this.users();
    if (!allUsers || allUsers.length === 0) {
      alert('No users to export.');
      return;
    }

    const toRow = (user: User, index: number) => ({
      'SL':           index + 1,
      'Full Name':    user.displayName || '—',
      'Email':        user.email || '—',
      'Role':         (user.role || '').toString().toUpperCase(),
      'Employee ID':  user.employeeId || '—',
      'Departments':  user.departmentIds?.map(id => this.getDepartmentName(id)).join(', ') || '—',
      'Block / NAC':  user.blockAccess?.length ? user.blockAccess.join(', ') : '—',
      'Mobile':       user.mobileNumber || '—',
      'Status':       user.isActive ? 'Active' : 'Inactive',
      'Created':      this.formatDate(user.createdAt),
      'Last Login':   user.lastLogin ? this.formatDate(user.lastLogin) : 'Never',
      'Remarks':      user.remarks || '—',
    });

    const wb = XLSX.utils.book_new();

    // Overview sheet — everyone, regardless of role
    const overview = allUsers.map((u, i) => toRow(u, i));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(overview), 'All Users');

    // One sheet per role — only added if there is at least one user of that role.
    // Excel sheet names are capped at 31 chars and can't contain : \ / ? * [ ]
    const roleSheets: { label: string; role: UserRole }[] = [
      { label: 'Admins',    role: UserRole.ADMIN },
      { label: 'Ministers', role: UserRole.MINISTER },
      { label: 'OSDs',      role: UserRole.OSD },
      { label: 'Operators', role: UserRole.OPERATOR },
      { label: 'Approvers', role: UserRole.APPROVER },
    ];
    for (const { label, role } of roleSheets) {
      const rows = allUsers.filter(u => u.role === role).map((u, i) => toRow(u, i));
      if (rows.length === 0) continue;
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), label);
    }

    const stamp = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `user-management-${stamp}.xlsx`);
  }

  // Enhanced pagination
  previousPage(): void {
    if (this.currentPage() > 1) {
      this.currentPage.update(p => p - 1);
    }
  }

  nextPage(): void {
    const totalPages = this.totalPages();
    if (this.currentPage() < totalPages.length) {
      this.currentPage.update(p => p + 1);
    }
  }

  goToPage(page: number): void {
    this.currentPage.set(page);
  }

  
  // Sorting methods
  sortBy(field: string): void {
    if (this.sortField() === field) {
      // Toggle sort direction if same field
      this.sortDirection.update(dir => dir === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to desc
      this.sortField.set(field);
      this.sortDirection.set('desc');
    }
  }

  

  ngOnInit(): void {
    this.loadData();
    this.userForm.get('employeeId')?.clearValidators();
    this.userForm.get('employeeId')?.updateValueAndValidity({ emitEvent: false });
  }

  async loadData() {
    this.loading.set(true);
    console.log('🔄 Starting to load user data...');

    // Always populate departments from pre-defined list immediately
    const predefined: Department[] = this.deptSchemesService.getDepartments().map(d => ({
      id: d.name.toLowerCase().replace(/[\s&]/g, '_').replace(/[^a-z0-9_]/g, ''),
      name: d.name,
      isActive: true,
      createdAt: null
    }));
    this.departments.set(predefined);
    console.log('📁 Departments loaded:', predefined.length);

    try {
      // Set up real-time listeners for automatic updates
      this.setupRealtimeListeners();
      
      // Initial data load
      console.log('👥 Fetching all users from Firestore...');
      const allUsers = await this.userService.getAllUsers();
      console.log('📊 Users fetched:', allUsers.length, allUsers);
      this.users.set(allUsers);

      // Fix any existing approverWorks roles automatically handled in normalizeRole

      // Override with Firestore departments only if they exist
      const allDepts = await this.deptService.getDepartments().catch(() => []);
      if (allDepts.length > 0) {
        this.departments.set(allDepts);
      }

      void this.locationMaster.ensureLoaded();

      // Get current user info for security checks
      const currentUser = await this.getCurrentUser();
      if (currentUser) {
        this.currentUserRole.set(currentUser.role);
        this.currentUserId.set(currentUser.uid);
        if (currentUser.role === UserRole.ADMIN) {
          await this.runLegacyDepartmentMigration(allUsers);
        }
      }
    } catch (error) {
      console.error('❌ Error loading users:', error);
    } finally {
      this.loading.set(false);
      console.log('✅ Data loading completed. Users count:', this.users().length);
    }
  }

  private setupRealtimeListeners(): void {
    // Real-time listener for users collection.
    // `idField: 'uid'` injects the Firestore doc id into each row's `uid` field
    // — without this the row's uid is undefined and downstream `updateDoc(usersCollection, uid)`
    // throws "Function doc() cannot be called with an empty path."
    const usersCollection = collection(this.firestore, 'users');
    const users$ = this.run(() =>
      collectionData(usersCollection, { idField: 'uid' }) as Observable<any[]>
    ).pipe(
      map(docs => docs.map(doc => ({
        uid: doc.uid,                                            // now reliably the Firestore doc id
        email: doc.email,
        displayName: doc.displayName,
        mobileNumber: doc.mobileNumber || doc.phoneNumber,
        employeeId: doc.employeeId,
        role: this.normalizeRole(doc.role, doc.uid, doc.email),
        departmentIds: doc.departmentIds || [],
        blockAccess: doc.blockAccess || [],
        assignedBy: doc.assignedBy,
        lastModifiedBy: doc.lastModifiedBy,
        assignmentAudit: doc.assignmentAudit || [],
        createdAt: doc.createdAt?.toDate?.() || new Date(),
        lastLogin: doc.lastLogin?.toDate?.(),
        isActive: doc.isActive ?? true,
        remarks: doc.remarks,
        assignedDepartments: doc.assignedDepartments || [],
        assignedSchemes: doc.assignedSchemes || [],
        assignedApproverIds: doc.assignedApproverIds || [],
        assignedOperators: doc.assignedOperators || [],
        approvalLevel: doc.approvalLevel,
        approvalScope: doc.approvalScope,
        permissions: doc.permissions || {}
      }))),
      takeUntil(this.destroy$)
    );

    // Subscribe to real-time updates
    users$.subscribe((users: User[]) => {
      console.log('🔄 Real-time user update received:', users.length, 'users');
      console.log('📋 User data:', users);
      this.users.set(users);
    });
  }

  private normalizeRole(rawRole: any, uid?: string, email?: string): UserRole {
    // Handle minister role restoration
    if (
      uid &&
      email === 'minister@nimbletech.co.in' &&
      rawRole === 'osd'
    ) {
      this.run(() => updateDoc(doc(this.usersCollection, uid), { role: 'minister' }))
        .catch(err => console.warn('[users] minister role restoration failed:', err));
      return UserRole.MINISTER;
    }
    
    // Handle approver role variations
    if (rawRole === 'approverWorks' || rawRole === 'approver') {
      // Fix the role in database if it's 'approverWorks'
      if (rawRole === 'approverWorks' && uid) {
        this.run(() => updateDoc(doc(this.usersCollection, uid), { role: 'approver' }))
          .catch(err => console.warn('[users] approver role normalization failed:', err));
      }
      return UserRole.APPROVER;
    }
    
    return rawRole as UserRole;
  }

  private normalizeDepartmentKey(value: string | null | undefined): string {
    return (value || '')
      .toLowerCase()
      .replace(/[\s&]/g, '_')
      .replace(/[^a-z0-9_]/g, '')
      .replace(/_+/g, '_')
      .trim();
  }

  private normalizeLegacyDepartmentIds(departmentIds: string[] | undefined): string[] {
    if (!Array.isArray(departmentIds) || departmentIds.length === 0) {
      return [];
    }

    const departments = this.departments();
    const normalized = departmentIds.map((rawId) => {
      const rawKey = this.normalizeDepartmentKey(rawId);
      const match = departments.find((dept) => {
        const idKey = this.normalizeDepartmentKey(dept.id);
        const nameKey = this.normalizeDepartmentKey(dept.name);
        return rawKey === idKey || rawKey === nameKey;
      });
      return (match?.name ?? rawId)?.trim();
    }).filter(Boolean);

    return Array.from(new Set(normalized));
  }

  private async runLegacyDepartmentMigration(users: User[]): Promise<void> {
    if (localStorage.getItem(this.legacyDepartmentMigrationKey) === 'done') {
      return;
    }

    let updatedCount = 0;
    for (const user of users) {
      const oldIds = user.departmentIds ?? [];
      const normalizedIds = this.normalizeLegacyDepartmentIds(oldIds);
      if (JSON.stringify(oldIds) === JSON.stringify(normalizedIds)) {
        continue;
      }

      try {
        await this.run(() => updateDoc(doc(this.usersCollection, user.uid), {
          departmentIds: normalizedIds,
          updatedAt: serverTimestamp()
        }));
        updatedCount += 1;
      } catch (error) {
        console.warn('[user-mgmt] legacy department migration failed for user:', user.uid, error);
      }
    }

    localStorage.setItem(this.legacyDepartmentMigrationKey, 'done');
    if (updatedCount > 0) {
      console.log(`[user-mgmt] legacy department migration completed. Updated users: ${updatedCount}`);
    }
  }

  private async fixApproverRoles(users: User[]): Promise<void> {
    const approverWorksUsers = users.filter(u => (u.role as string) === 'approverWorks');
    
    for (const user of approverWorksUsers) {
      try {
        await this.run(() => updateDoc(doc(this.usersCollection, user.uid), { 
          role: 'approver' 
        }));
        console.log(`Fixed role for user ${user.displayName}: approverWorks -> approver`);
      } catch (error) {
        console.error(`Failed to fix role for user ${user.displayName}:`, error);
      }
    }
  }

  openCreateModal() {
    void this.locationMaster.ensureLoaded();
    this.isBlockMenuOpen.set(false);
    this.editingUser = null;
    this.userForm.reset({
      displayName: '',
      email: '',
      mobileNumber: '',
      employeeId: '',
      remarks: '',
      password: '',
      role: UserRole.OPERATOR,
      departmentIds: [],
      blockAccess: [],
      status: 'active',
    });
    // Password required for new users
    this.userForm.get('password')?.setValidators([Validators.required, Validators.minLength(6)]);
    this.userForm.updateValueAndValidity();
    this.isModalOpen.set(true);
  }

  openEditModal(user: User) {
    void this.locationMaster.ensureLoaded();
    this.isBlockMenuOpen.set(false);
    const latest = this.users().find(u => u.uid === user.uid) ?? user;
    this.editingUser = latest;
    this.userForm.patchValue({
      displayName: latest.displayName,
      email: latest.email,
      mobileNumber: latest.mobileNumber ?? '',
      employeeId: (latest.role === UserRole.OPERATOR || latest.role === UserRole.APPROVER)
        ? ''
        : (latest.employeeId ?? ''),
      role: latest.role,
      departmentIds: this.resolveDepartmentIdsForForm(latest.departmentIds ?? []),
      blockAccess: latest.blockAccess ?? [],
      remarks: latest.remarks ?? '',
      password: '',
      status: latest.isActive ? 'active' : (latest.isActive === false ? 'inactive' : 'suspended')
    });
    // Password optional for edits — only validate length if the admin enters a new one
    this.userForm.get('password')?.setValidators([Validators.minLength(6)]);
    this.userForm.get('password')?.updateValueAndValidity();
    this.userForm.updateValueAndValidity();
    this.isModalOpen.set(true);
  }

  openDeleteModal(user: User) {
    this.userToDelete.set(user);
    this.isDeleteModalOpen.set(true);
  }

  onSearchChange(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchQuery.set(value);
    this.currentPage.set(1);
  }

  setRoleFilter(role: string): void {
    this.roleFilter.set(role);
    this.currentPage.set(1);
  }

  formatRoleLabel(role: string): string {
    if (role === 'approverWorks') return 'Approver';
    return role.charAt(0).toUpperCase() + role.slice(1);
  }

  blocksSummary(user: User): string {
    const list = user.blockAccess ?? [];
    if (!list.length) return '—';
    if (list.length <= 2) return list.join(', ');
    return `${list.slice(0, 2).join(', ')} +${list.length - 2}`;
  }

  departmentsSummary(user: User): string {
    const ids = user.departmentIds ?? [];
    if (!ids.length) return '—';
    const names = ids.map(id => this.getDepartmentName(id)).filter(n => n !== '—' && n !== 'Unknown');
    if (!names.length) return '—';
    if (names.length <= 2) return names.join(', ');
    return `${names.slice(0, 2).join(', ')} +${names.length - 2}`;
  }

  getUserRowClass(user: User): string {
    return user.isActive ? 'row-active' : 'row-inactive';
  }

  getUserCardClass(user: User): string {
    return user.isActive ? 'card-active' : 'card-inactive';
  }

  getRoleClass(role: string): string {
    switch (role) {
      case 'admin': return 'role-admin';
      case 'minister': return 'role-minister';
      case 'osd': return 'role-osd';
      case 'operator': return 'role-operator';
      case 'approver': 
      case 'approverWorks': return 'role-approver';
      default: return 'role-default';
    }
  }

  getStatusClass(user: User): string {
    return user.isActive ? 'status-active' : 'status-inactive';
  }

  getDepartmentName(deptId: string): string {
    if (!deptId) return '—';
    const normalized = deptId.toLowerCase().trim();
    const dept = this.departments().find(d =>
      d.id === deptId || d.name === deptId || d.name.toLowerCase() === normalized
    );
    return dept ? dept.name : 'Unknown';
  }

  formatDate(date: string | Date | null | undefined): string {
    if (!date) return '—';
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString();
  }

  updateFilteredUsers(): void {
    // The filtering is handled by the computed property filteredUsers()
    // This method is called when showInactiveUsers changes, but the computed
    // property automatically updates based on the signal value
    // No additional logic needed here
  }

  async submitForm() {
    if (this.userForm.invalid) {
      this.markFormGroupTouched(this.userForm);
      if (this.userForm.hasError('departmentRequired')) {
        alert('Please assign at least one department.');
      } else if (this.userForm.hasError('blockAccessRequired')) {
        alert('Please assign at least one Block / NAC.');
      } else if (this.userForm.hasError('mismatch')) {
        alert('Passwords do not match.');
      } else {
        alert('Please fill in all required fields correctly.');
      }
      return;
    }

    // Security check: only admins can create/edit users
    if (this.currentUserRole() !== UserRole.ADMIN) {
      alert('Only administrators can manage users.');
      return;
    }

    this.isSubmitting.set(true);
    const wasEdit = !!this.editingUser;
    try {
      const formData = { ...this.userForm.value };
      formData.departmentIds = this.normalizeAssignedDepartments(formData.departmentIds);
      formData.blockAccess = this.normalizeBlockAccess(formData.blockAccess);
      formData.email = String(formData.email ?? '').trim().toLowerCase();

      const adminUid = this.currentUserId() || this.currentUser()?.uid || 'system';
      const auditEntry: UserAssignmentAuditEntry = {
        timestamp: Timestamp.now(),
        action: this.editingUser ? 'updated' : 'created',
        assignedBy: adminUid,
        departments: formData.departmentIds,
        blockAccess: formData.blockAccess,
        details: this.editingUser
          ? `Updated assignments for ${formData.displayName}`
          : `Created user ${formData.displayName}`,
      };

      const newPassword = String(formData.password ?? '').trim();
      delete formData.password;
      let passwordResult: 'set' | 'email-sent' | null = null;

      if (this.editingUser) {
        const prior = this.editingUser.assignmentAudit ?? [];
        formData.assignmentAudit = [...prior, auditEntry];
        formData.lastModifiedBy = adminUid;
        formData.assignedDepartments = formData.departmentIds;
        formData.assignedSchemes = this.editingUser.assignedSchemes ?? [];
        formData.assignedApproverIds = this.editingUser.assignedApproverIds ?? [];
        formData.assignedOperators = this.editingUser.assignedOperators ?? [];
        formData.approvalLevel = this.editingUser.approvalLevel ?? null;
        formData.approvalScope = this.editingUser.approvalScope ?? null;
        formData.permissions = this.editingUser.permissions ?? {};
      } else {
        formData.assignedBy = adminUid;
        formData.lastModifiedBy = adminUid;
        formData.assignmentAudit = [auditEntry];
        formData.assignedDepartments = formData.departmentIds;
      }

      if (formData.role === UserRole.OPERATOR || formData.role === UserRole.APPROVER) {
        formData.employeeId = null;
      }
      
      // Convert status to boolean
      formData.isActive = formData.status === 'active';
      delete formData.status;
      
      if (this.editingUser) {
        if (!this.editingUser.uid) {
          console.error('[user-mgmt] editingUser is missing uid', this.editingUser);
          alert('Cannot update: this user record is missing its Firestore id. Reload the page and try again.');
          return;
        }
        const oldRole = this.editingUser.role;
        const newRole = formData.role;
        const editingUid = this.editingUser.uid;

        await this.userService.updateUser(editingUid, formData);

        if (oldRole !== newRole) {
          await this.handleRoleChange(editingUid, oldRole, newRole);
        }

        await this.updateDepartmentAssignments(editingUid, formData.departmentIds, newRole);

        if (newPassword) {
          passwordResult = await this.userService.adminResetUserPassword(editingUid, formData.email, newPassword);
        }

        this.editingUser = null;
      } else {
        const createPassword = String(this.userForm.get('password')?.value ?? '');
        if (!createPassword || createPassword.length < 6) {
          alert('Password is required for new users (minimum 6 characters).');
          this.isSubmitting.set(false);
          return;
        }
        
        // Check for duplicate email in Firestore
        const existingUser = await this.userService.getUserByEmail(formData.email);
        if (existingUser) {
          alert('A user with this email already exists.');
          this.isSubmitting.set(false);
          return;
        }
        
        // Create Firebase Auth account (or recover UID if Auth exists without Firestore profile)
        const uid = await this.userService.obtainAuthUidForRegistration(formData.email, createPassword);
        await this.userService.createUser(uid, formData);
        
        // Handle initial role and department assignments
        await this.handleRoleChange(uid, null, formData.role);
        await this.updateDepartmentAssignments(uid, formData.departmentIds, formData.role);
      }
      
      this.isModalOpen.set(false);
      this.closeBlockMenu();
      await this.loadData();
      if (passwordResult === 'set') {
        alert('User updated successfully! Password has been changed.');
      } else if (passwordResult === 'email-sent') {
        alert('User updated successfully! Could not set the password directly, so a password reset email was sent to the user instead.');
      } else {
        alert(wasEdit ? 'User updated successfully!' : 'User created successfully!');
      }
    } catch (error: any) {
      console.error('Error saving user:', error);
      const message = error?.message || 'Unable to save user. Please try again.';
      alert(`Error: ${message}`);
    } finally {
      this.isSubmitting.set(false);
    }
  }

  private normalizeAssignedDepartments(departmentIds: string[] | null | undefined): string[] {
    if (!Array.isArray(departmentIds) || departmentIds.length === 0) {
      return [];
    }

    const mapped = departmentIds.map((rawId) => {
      const dept = this.departments().find(d => d.id === rawId || d.name === rawId);
      // Persist department name so operator dashboards can resolve scope consistently.
      return (dept?.name ?? rawId)?.trim();
    }).filter(Boolean);

    return Array.from(new Set(mapped));
  }

  private normalizeBlockAccess(blockAccess: string[] | null | undefined): string[] {
    if (!Array.isArray(blockAccess) || blockAccess.length === 0) return [];
    const known = new Set(this.masterLocations().map(l => l.name));
    return Array.from(new Set(blockAccess.filter(name => known.has(name))));
  }

  /** Map stored department names/ids to form checkbox ids. */
  private resolveDepartmentIdsForForm(rawIds: string[]): string[] {
    if (!Array.isArray(rawIds) || rawIds.length === 0) return [];
    const resolved = rawIds.map(raw => {
      const key = String(raw ?? '').trim();
      if (!key) return '';
      const dept = this.departments().find(d =>
        d.id === key ||
        d.name === key ||
        d.name.toLowerCase() === key.toLowerCase()
      );
      return dept?.id ?? key;
    }).filter(Boolean);
    return Array.from(new Set(resolved));
  }

  isDepartmentSelected(deptId: string): boolean {
    const current: string[] = this.userForm.get('departmentIds')?.value || [];
    const dept = this.departments().find(d => d.id === deptId);
    return current.includes(deptId) ||
      (dept != null && current.some(v =>
        v === dept.name || v.toLowerCase() === dept.name.toLowerCase()
      ));
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if ((control as any).controls) {
        this.markFormGroupTouched(control as FormGroup);
      }
    });
  }

  async getCurrentUser(): Promise<User | null> {
    try {
      return this.authService.getCurrentUser();
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  }

  async handleRoleChange(uid: string, oldRole: UserRole | null, newRole: UserRole): Promise<void> {
    try {
      console.log(`Handling role change for user ${uid}: ${oldRole} -> ${newRole}`);
      
      // Remove old role permissions
      if (oldRole) {
        await this.removeRolePermissions(uid, oldRole);
      }
      
      // Assign new role permissions
      await this.assignRolePermissions(uid, newRole);
      
      // Update role mappings collection
      await this.updateRoleMappings(uid, oldRole, newRole);
      
    } catch (error) {
      console.error('Error handling role change:', error);
      throw error;
    }
  }

  async removeRolePermissions(uid: string, role: UserRole | string): Promise<void> {
    // Remove from role-specific collections
    const firestore = this.firestore;
    
    // Handle string role values as well
    const normalizedRole = role === 'approverWorks' ? UserRole.APPROVER : role;
    
    switch (normalizedRole) {
      case UserRole.OPERATOR:
        // Remove from operator-specific collections
        await this.run(() => updateDoc(doc(firestore, 'users', uid), {
          assignedSchemes: [],
          assignedApproverIds: []
        }));
        break;
      
      case UserRole.APPROVER:
        // Remove from approver-specific collections
        await this.run(() => updateDoc(doc(firestore, 'users', uid), {
          assignedOperators: [],
          approvalLevel: null,
          approvalScope: null
        }));
        break;
    }
  }

  async assignRolePermissions(uid: string, role: UserRole | string): Promise<void> {
    const firestore = this.firestore;
    
    // Handle string role values as well
    const normalizedRole = role === 'approverWorks' ? UserRole.APPROVER : role;
    
    switch (normalizedRole) {
      case UserRole.OPERATOR:
        // Initialize operator-specific fields
        await this.run(() => updateDoc(doc(firestore, 'users', uid), {
          assignedSchemes: [],
          assignedApproverIds: []
        }));
        break;
      
      case UserRole.APPROVER:
        // Initialize approver-specific fields
        await this.run(() => updateDoc(doc(firestore, 'users', uid), {
          assignedOperators: [],
          approvalLevel: 'level1',
          approvalScope: 'single'
        }));
        break;
    }
  }

  async updateRoleMappings(uid: string, oldRole: UserRole | null, newRole: UserRole): Promise<void> {
    const firestore = this.firestore;
    const roleMappingsCollection = collection(firestore, 'roleMappings');
    
    try {
      // Remove old role mapping
      if (oldRole) {
        const oldMappingQuery = query(roleMappingsCollection, 
          where('userId', '==', uid), 
          where('role', '==', oldRole)
        );
        const oldMappingSnapshot = await getDocs(oldMappingQuery);
        
        for (const doc of oldMappingSnapshot.docs) {
          await this.run(() => deleteDoc(doc.ref));
        }
      }
      
      // Add new role mapping
      await this.run(() => setDoc(doc(roleMappingsCollection, `${uid}_${newRole}`), {
        userId: uid,
        role: newRole,
        assignedAt: serverTimestamp(),
        isActive: true
      }));
      
    } catch (error) {
      console.error('Error updating role mappings:', error);
      throw error;
    }
  }

  async updateDepartmentAssignments(uid: string, departmentIds: string[], role: UserRole): Promise<void> {
    const firestore = this.firestore;
    const deptAssignmentsCollection = collection(firestore, 'departmentAssignments');
    
    try {
      // Clear existing department assignments
      const existingAssignmentsQuery = query(deptAssignmentsCollection, where('userId', '==', uid));
      const existingSnapshot = await getDocs(existingAssignmentsQuery);
      
      for (const doc of existingSnapshot.docs) {
        await this.run(() => deleteDoc(doc.ref));
      }
      
      // Add new department assignments (use stable slug ids for doc keys)
      for (const deptId of departmentIds) {
        const dept = this.departments().find(d => d.id === deptId || d.name === deptId);
        const assignmentKey = dept?.id ?? deptId.toLowerCase().replace(/[\s&]/g, '_').replace(/[^a-z0-9_]/g, '');
        await this.run(() => setDoc(doc(deptAssignmentsCollection, `${uid}_${assignmentKey}`), {
          userId: uid,
          departmentId: dept?.name ?? deptId,
          role: role,
          assignedAt: serverTimestamp(),
          isActive: true
        }));
      }
      
    } catch (error) {
      console.error('Error updating department assignments:', error);
      throw error;
    }
  }

  async confirmDelete(): Promise<void> {
    if (!this.userToDelete()) return;
    
    this.isDeleting.set(true);
    try {
      const user = this.userToDelete()!;
      
      // Security check: only admins can delete
      if (this.currentUserRole() !== UserRole.ADMIN) {
        throw new Error('Only administrators can delete users.');
      }
      
      // Security check: prevent self-deletion
      if (user.uid === this.currentUserId()) {
        throw new Error('You cannot delete your own account.');
      }
      
      // Delete from all collections
      await this.deleteUserCompletely(user.uid);
      
      this.isDeleteModalOpen.set(false);
      this.userToDelete.set(null);
      await this.loadData();
      alert('User deleted successfully!');
      
    } catch (error: any) {
      console.error('Error deleting user:', error);
      alert(`Error: ${error.message || 'Failed to delete user.'}`);
    } finally {
      this.isDeleting.set(false);
    }
  }

  async deleteUserCompletely(uid: string): Promise<void> {
    const firestore = this.firestore;
    
    try {
      // Delete from users collection
      await this.run(() => deleteDoc(doc(firestore, 'users', uid)));
      
      // Delete from roleMappings collection
      const roleMappingsCollection = collection(firestore, 'roleMappings');
      const roleMappingsQuery = query(roleMappingsCollection, where('userId', '==', uid));
      const roleMappingsSnapshot = await getDocs(roleMappingsQuery);
      
      for (const doc of roleMappingsSnapshot.docs) {
        await this.run(() => deleteDoc(doc.ref));
      }
      
      // Delete from departmentAssignments collection
      const deptAssignmentsCollection = collection(firestore, 'departmentAssignments');
      const deptAssignmentsQuery = query(deptAssignmentsCollection, where('userId', '==', uid));
      const deptAssignmentsSnapshot = await getDocs(deptAssignmentsQuery);
      
      for (const doc of deptAssignmentsSnapshot.docs) {
        await this.run(() => deleteDoc(doc.ref));
      }
      
      // Delete from permissions collection
      const permissionsCollection = collection(firestore, 'permissions');
      const permissionsQuery = query(permissionsCollection, where('userId', '==', uid));
      const permissionsSnapshot = await getDocs(permissionsQuery);
      
      for (const doc of permissionsSnapshot.docs) {
        await this.run(() => deleteDoc(doc.ref));
      }
      
      // Delete from approvalMappings collection (if exists)
      try {
        const approvalMappingsCollection = collection(firestore, 'approvalMappings');
        const approvalMappingsQuery = query(approvalMappingsCollection, where('userId', '==', uid));
        const approvalMappingsSnapshot = await getDocs(approvalMappingsQuery);
        
        for (const doc of approvalMappingsSnapshot.docs) {
          await this.run(() => deleteDoc(doc.ref));
        }
      } catch (error) {
        // Collection might not exist, which is fine
        console.log('ApprovalMappings collection does not exist or is empty');
      }
      
    } catch (error) {
      console.error('Error deleting user completely:', error);
      throw error;
    }
  }

  
  showSuccessMessage(message: string): void {
    console.log('Success:', message);
  }

  showErrorMessage(message: string): void {
    console.error('Error:', message);
  }

  // Department selection for form
  toggleDepartmentSelection(deptId: string): void {
    const currentDeptIds: string[] = [...(this.userForm.get('departmentIds')?.value || [])];
    const dept = this.departments().find(d => d.id === deptId);
    const normalizedCurrent = this.resolveDepartmentIdsForForm(currentDeptIds);

    if (normalizedCurrent.includes(deptId)) {
      const next = currentDeptIds.filter(id => {
        if (id === deptId) return false;
        if (dept && (id === dept.name || id.toLowerCase() === dept.name.toLowerCase())) return false;
        return true;
      });
      this.userForm.get('departmentIds')?.setValue(next);
    } else {
      this.userForm.get('departmentIds')?.setValue([...currentDeptIds, deptId]);
    }
    this.userForm.get('departmentIds')?.markAsTouched();
    this.userForm.updateValueAndValidity();
  }

  toggleBlockSelection(locationName: string): void {
    const current = this.userForm.get('blockAccess')?.value || [];
    const next = [...current];
    const index = next.indexOf(locationName);
    if (index > -1) next.splice(index, 1);
    else next.push(locationName);
    this.userForm.get('blockAccess')?.setValue(next);
    this.userForm.get('blockAccess')?.markAsTouched();
  }

  isBlockSelected(locationName: string): boolean {
    return (this.userForm.get('blockAccess')?.value || []).includes(locationName);
  }

  selectedBlockCount(): number {
    return (this.userForm.get('blockAccess')?.value || []).length;
  }

  blockAccessSelectionLabel(): string {
    const selected: string[] = this.userForm.get('blockAccess')?.value || [];
    const total = this.masterLocations().length;
    if (!selected.length) return 'Select Block / NAC';
    if (total > 0 && selected.length === total) return 'All Blocks / NACs';
    if (selected.length === 1) return selected[0];
    return `${selected[0]} +${selected.length - 1}`;
  }

  toggleBlockMenu(event?: Event): void {
    event?.stopPropagation();
    this.isBlockMenuOpen.update(open => !open);
  }

  closeBlockMenu(): void {
    this.isBlockMenuOpen.set(false);
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.isBlockMenuOpen.set(false);
  }

  selectAllBlocks(): void {
    const all = this.masterLocations().map(l => l.name);
    this.userForm.get('blockAccess')?.setValue(all);
    this.userForm.get('blockAccess')?.markAsTouched();
  }

  clearBlockSelection(): void {
    this.userForm.get('blockAccess')?.setValue([]);
    this.userForm.get('blockAccess')?.markAsTouched();
  }

  // Debug method to seed test users
  async seedTestUsers(): Promise<void> {
    console.log('🌱 Seeding test users...');
    try {
      await this.userService.seedMasterAccounts();
      console.log('✅ Test users seeded successfully');
      await this.loadData(); // Refresh the data
    } catch (error) {
      console.error('❌ Error seeding test users:', error);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
